import React from 'react';

import { StandaloneLabel } from '../../entities/standalone-project';
import { StandaloneTask } from '../../entities/standalone-task';
import { contrastTextColor } from '../../shared/utils/color';
import { AgentAuthorBadge } from '../ui/AgentAuthorBadge';

const PRIORITY_BADGE: Record<string, string> = {
  high: 'badge-error',
  medium: 'badge-warning',
  low: 'badge-ghost',
};

interface TaskCardProps {
  task: StandaloneTask;
  labels: StandaloneLabel[]; // project.labels — resolves task.labelIds to name/color
  subtaskTotal: number;
  subtaskDone: number;
  isDoneCategory: boolean; // whether task.status's category is 'done' — drives the overdue check
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  isDragging: boolean;
  // Drag-and-drop reordering (standalone-contract.md "Task ordering") — `dropIndicator` renders a
  // thin insertion line above/below this card while another task is being dragged over it.
  dropIndicator?: 'before' | 'after' | null;
  onCardDragOver?: (e: React.DragEvent) => void;
  onCardDrop?: (e: React.DragEvent) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  labels,
  subtaskTotal,
  subtaskDone,
  isDoneCategory,
  onClick,
  onDragStart,
  onDragEnd,
  isDragging,
  dropIndicator,
  onCardDragOver,
  onCardDrop,
}) => {
  const isOverdue = !isDoneCategory && !!task.dueDate && new Date(task.dueDate) < new Date();
  const taskLabels = (task.labelIds ?? [])
    .map((id) => labels.find((l) => l.id === id))
    .filter((l): l is StandaloneLabel => !!l);

  return (
    <div
      className={`card bg-base-100 shadow-card hover:-translate-y-0.5 transition cursor-pointer ${
        isDragging ? 'opacity-40' : ''
      } ${dropIndicator === 'before' ? 'border-t-2 border-primary' : ''} ${
        dropIndicator === 'after' ? 'border-b-2 border-primary' : ''
      }`}
      role="button"
      tabIndex={0}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onCardDragOver}
      onDrop={onCardDrop}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="card-body p-3 gap-1.5">
        {taskLabels.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            {taskLabels.map((label) => (
              <span
                key={label.id}
                className="badge badge-xs border-0"
                style={{ backgroundColor: label.color || '#6b7280', color: contrastTextColor(label.color) }}
              >
                {label.name}
              </span>
            ))}
          </div>
        )}
        <div className="text-sm font-medium">
          <span className="font-mono text-xs opacity-40 mr-1.5">{task.key}</span>
          {task.title}
        </div>
        {task.description && <div className="text-xs opacity-60 line-clamp-2">{task.description}</div>}
        {(task.priority || task.dueDate || subtaskTotal > 0 || task.authorSource === 'mcp_agent') && (
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
            {task.priority && (
              <span className={`badge badge-xs ${PRIORITY_BADGE[task.priority] ?? 'badge-ghost'}`}>
                {task.priority}
              </span>
            )}
            {task.dueDate && (
              <span className={`badge badge-xs badge-outline ${isOverdue ? 'text-error border-error' : ''}`}>
                {new Date(task.dueDate).toLocaleDateString()}
              </span>
            )}
            {subtaskTotal > 0 && (
              <span className="badge badge-xs badge-ghost">
                {subtaskDone}/{subtaskTotal} subtasks
              </span>
            )}
            {task.authorSource === 'mcp_agent' && <AgentAuthorBadge clientName={task.clientName} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCard;
